/* eslint-disable max-lines */

import gql from 'graphql-tag';

import {
  accountEventObjectFields,
  accountFields,
  addressFields,
  adminUserEventFields,
  adminUserFields,
  affiliateFields,
  affiliateInvoiceFields,
  affiliateMerchantFields,
  authorizedUserFields,
  blacklistItemFields,
  chargeEventObjectFields,
  chargeFields,
  chargesExportFields,
  codeFields,
  documentEventFields,
  documentFields,
  masterAccountEventObjectFields,
  masterAccountFields,
  payoutMethodsFields,
  pibisiAccountFields,
  pibisiEventFields,
  pointOfSaleFields,
  providerEventObjectFields,
  providerFields,
  sftpConfigExportFields,
  storeFields,
  subscriptionEventObjectFields,
  subscriptionFields,
  traceDetailsFields,
  userFields,
  userMasterAccountFields
} from 'api/fragments';

export const accountQuery = gql`
  query Account($id: ID!) {
    account(id: $id) {
      ...AccountFields
    }
  }
  ${accountFields}
`;

export const accountsQuery = gql`
  query Accounts($filter: SearchableAccountFilterInput, $search: String, $size: Int, $from: Int) {
    accounts(filter: $filter, search: $search, size: $size, from: $from) {
      items {
        ...AccountFields
      }
      total
    }
  }
  ${accountFields}
`;

export const authorizedUsersQuery = gql`
  query AuthorizedUsers($accountId: ID!) {
    authorizedUsers(accountId: $accountId) {
      items {
        ...AuthorizedUserFields
      }
    }
  }
  ${authorizedUserFields}
`;

export const availablePaymentMethodsQuery = gql`
  query AvailablePaymentMethods($accountId: ID!) {
    availablePaymentMethods(accountId: $accountId) {
      paymentMethod
      configured
      enabled
    }
  }
`;
export const availableProcessorsQuery = gql`
  query AvailableProcessors($accountId: ID!, $paymentMethod: PaymentMethods!) {
    availableProcessors(accountId: $accountId, paymentMethod: $paymentMethod) {
      paymentMethod
      processor
      configured
      enabled
    }
  }
`;

export const providerConfigQuery = gql`
  query ProviderConfig($paymentMethod: PaymentMethods!, $processor: Processor!) {
    providerConfig(paymentMethod: $paymentMethod, processor: $processor) {
      paymentMethod
      processor
      acquirers
      metadataConfig {
        key
        type
        label
        possibleValues
        default
        regex
        secret
        example
      }
    }
  }
`;

export const providerQuery = gql`
  query Provider($id: ID!) {
    provider(id: $id) {
      ...ProviderFields
    }
  }
  ${providerFields}
`;

export const providersQuery = gql`
  query Providers($accountId: ID!) {
    providers(accountId: $accountId) {
      ...ProviderFields
    }
  }
  ${providerFields}
`;

export const chargesQuery = gql`
  query Charges($filter: SearchableChargeFilterInput, $search: String, $size: Int, $from: Int) {
    charges(filter: $filter, search: $search, size: $size, from: $from) {
      items {
        ...ChargeFields
      }
      total
    }
  }
  ${chargeFields}
`;

export const chargesExportQuery = gql`
  query ChargesExport($id: ID!) {
    chargesExport(id: $id) {
      ...ChargesExportFields
    }
  }
  ${chargesExportFields}
`;

export const chargeQuery = gql`
  query Charge($id: ID!) {
    charge(id: $id) {
      ...ChargeFields
    }
  }
  ${chargeFields}
`;

export const succeededDateRangeKpiQuery = gql`
  query SucceededDateRangeKpi(
    $start: Int
    $end: Int
    $interval: Interval
    $accountId: ID
    $acquirer: Acquirer
    $masterAccountId: ID
    $billingPlan: String
    $paymentMethod: PaymentMethods
    $source: String
    $timezone: String
    $currency: Currencies
  ) {
    chargesDateRangeKPI(
      start: $start
      end: $end
      interval: $interval
      accountId: $accountId
      acquirer: $acquirer
      masterAccountId: $masterAccountId
      billingPlan: $billingPlan
      paymentMethod: $paymentMethod
      source: $source
      timezone: $timezone
      currency: $currency
    ) {
      currency
      total {
        succeededAmount
        succeededCount
      }
      data {
        timestamp
        cuSucceededAmount
        cuSucceededCount
      }
    }
  }
`;

export const chargesDateRangeKpiQuery = gql`
  query ChargesDateRangeKpi(
    $start: Int
    $end: Int
    $interval: Interval
    $accountId: ID
    $acquirer: Acquirer
    $masterAccountId: ID
    $billingPlan: String
    $paymentMethod: PaymentMethods
    $source: String
    $timezone: String
    $currency: Currencies
  ) {
    chargesDateRangeKPI(
      start: $start
      end: $end
      interval: $interval
      accountId: $accountId
      acquirer: $acquirer
      masterAccountId: $masterAccountId
      billingPlan: $billingPlan
      paymentMethod: $paymentMethod
      source: $source
      timezone: $timezone
      currency: $currency
    ) {
      currency
      total {
        succeededAmount
        succeededCount
        refundedAmount
        refundedCount
        failedCount
      }
      data {
        timestamp
        succeededAmount
        succeededCount
        refundedAmount
        refundedCount
        failedCount
      }
    }
  }
`;

export const chargeEventsQuery = gql`
  query ChargeEvents($chargeId: ID!, $size: Int, $from: Int) {
    chargeEvents(chargeId: $chargeId, size: $size, from: $from) {
      items {
        id
        type
        accountId
        livemode
        objectId
        objectType
        createdAt
        object {
          ...ChargeEventObjectFields
        }
      }
      total
    }
  }
  ${chargeEventObjectFields}
`;

export const eventsQuery = gql`
  query Events($filter: SearchableEventFilterInput, $size: Int, $from: Int) {
    events(filter: $filter, size: $size, from: $from) {
      items {
        id
        type
        accountId
        livemode
        objectId
        objectType
        createdAt
        object {
          ...ChargeEventObjectFields
        }
      }
      total
    }
  }
  ${chargeEventObjectFields}
`;

export const userQuery = gql`
  query User($id: ID!) {
    user(id: $id) {
      ...UserFields
    }
  }
  ${userFields}
`;
export const usersQuery = gql`
  query Users($filter: String, $token: String, $limit: Int) {
    users(filter: $filter, token: $token, limit: $limit) {
      items {
        ...UserFields
      }
      nextToken
    }
  }
  ${userFields}
`;

export const countriesQuery = gql`
  query Countries {
    countries {
      supportedCountries {
        name
        phoneCode
        code
      }
      currentCountry {
        name
        phoneCode
        code
      }
      currentCountryCode
      isSupported
    }
  }
`;

export const accountActualFeesQuery = gql`
  query AccountActualFees($id: ID!) {
    accountActualFees(id: $id) {
      highRiskFee
      dailyPlatformFee
      dailyExtraSeatsFee
      weeklyCustomDomainFee
      weeklySftpConfigUnitFee
      fraudDetectorTrxFlatFee
      cardPresentTrxFee
      cardPresentTrxFlatFee
      extra {
        name
        amount
        type
        region
        masterAccountFee
        paymentMethods
      }
      steps {
        from
        trxFee
        trxFlatFee
        bizumTrxFlatFee
        bizumTrxFee
      }
    }
  }
`;

export const accountEvents = gql`
  query AccountEvents($accountId: ID!, $size: Int, $from: Int) {
    accountEvents(accountId: $accountId, size: $size, from: $from) {
      items {
        id
        type
        accountId
        livemode
        objectId
        objectType
        object {
          ...AccountEventObjectFields
        }
        createdAt
      }
      total
    }
  }
  ${accountEventObjectFields}
`;

export const settlementsQuery = gql`
  query Settlements($filter: SearchableSettlementFilterInput, $size: Int, $from: Int) {
    settlements(filter: $filter, size: $size, from: $from) {
      items {
        ...SettlementFields
      }
      total
    }
  }
`;

export const accountInvoicesQuery = gql`
  query AccountInvoices($filter: SearchableInvoiceFilterInput, $size: Int, $from: Int) {
    accountInvoices(filter: $filter, size: $size, from: $from) {
      items {
        ...AccountInvoiceFields
      }
      total
    }
  }
`;

export const previewPaymentEmailQuery = gql`
  query PreviewPaymentEmail(
    $paymentId: ID!
    $emailType: PaymentEmailType
    $language: String
    $accountId: ID!
  ) {
    previewPaymentEmail(
      paymentId: $paymentId
      emailType: $emailType
      language: $language
      accountId: $accountId
    )
  }
`;

export const accountStatsQuery = gql`
  query AccountStats($accountId: ID!) {
    accountStats(accountId: $accountId) {
      stats {
        firstTransactionAt
        activeMonths
        avgMonthlyTransactions
        avgMonthlyVolume
        churned
        totalSucceededCount
        lastTransactionAt
        totalSucceededAmount
      }
    }
  }
`;

export const masterAccountsQuery = gql`
  query MasterAccounts($filter: SearchableMasterAccountFilterInput, $size: Int, $from: Int) {
    masterAccounts(filter: $filter, size: $size, from: $from) {
      items {
        ...MasterAccountFields
      }
      total
    }
  }
  ${masterAccountFields}
`;

export const storesQuery = gql`
  query Stores($accountId: ID!, $pageSize: Int, $token: String) {
    stores(accountId: $accountId, pageSize: $pageSize, token: $token) {
      items {
        ...StoreFields
      }
      nextToken
    }
  }
  ${storeFields}
`;

export const storeQuery = gql`
  query Store($id: ID!, $accountId: ID!) {
    store(id: $id, accountId: $accountId) {
      ...StoreFields
      blocked
      metadata {
        key
        value
      }
    }
  }
  ${storeFields}
`;

export const storeEventsQuery = gql`
  query StoreEvents($storeId: ID!, $size: Int, $from: Int) {
    storeEvents(storeId: $storeId, size: $size, from: $from) {
      items {
        id
        type
        accountId
        objectId
        objectType
        createdAt
        object {
          id
          accountId
          name
          email
          phone
          website
          iban
          documentNumber
          blocked
          address {
            ...AddressFields
          }
          traceDetails {
            ...TraceDetailsFields
          }
        }
      }
      total
    }
  }
  ${addressFields}
  ${traceDetailsFields}
`;

export const documentsQuery = gql`
  query Documents($accountId: ID!) {
    documents(accountId: $accountId) {
      items {
        ...DocumentFields
      }
    }
  }
  ${documentFields}
`;

export const documentQuery = gql`
  query Document($id: ID!, $accountId: ID!) {
    document(id: $id, accountId: $accountId) {
      ...DocumentFields
    }
  }
  ${documentFields}
`;

export const documentEventsQuery = gql`
  query DocumentEvents($documentId: ID!) {
    documentEvents(documentId: $documentId) {
      items {
        ...DocumentEventFields
      }
      total
    }
  }
  ${documentEventFields}
`;

export const subscriptionsQuery = gql`
  query Subscriptions(
    $filter: SearchableSubscriptionFilterInput
    $search: String
    $size: Int
    $from: Int
  ) {
    subscriptions(filter: $filter, search: $search, size: $size, from: $from) {
      items {
        ...SubscriptionFields
      }
      total
    }
  }
  ${subscriptionFields}
`;

export const subscriptionQuery = gql`
  query Subscription($id: ID!) {
    subscription(id: $id) {
      ...SubscriptionFields
    }
  }
  ${subscriptionFields}
`;

export const subscriptionEventsQuery = gql`
  query SubscriptionEvents($subscriptionId: ID!) {
    subscriptionEvents(subscriptionId: $subscriptionId) {
      items {
        id
        type
        accountId
        objectId
        objectType
        createdAt
        object {
          ...SubscriptionEventObjectFields
        }
      }
      total
    }
  }
  ${subscriptionEventObjectFields}
`;

export const previewSubscriptionEmailQuery = gql`
  query PreviewSubscriptionEmail(
    $subscriptionId: ID!
    $emailType: SubscriptionEmailType
    $language: String
    $accountId: ID!
  ) {
    previewSubscriptionEmail(
      subscriptionId: $subscriptionId
      emailType: $emailType
      language: $language
      accountId: $accountId
    )
  }
`;

export const providerEventsQuery = gql`
  query ProviderEvents($providerId: ID, $accountId: ID!) {
    providerEvents(providerId: $providerId, accountId: $accountId) {
      items {
        id
        type
        accountId
        objectId
        objectType
        createdAt
        object {
          ...ProviderEventObjectFields
        }
      }
      total
    }
  }
  ${providerEventObjectFields}
`;

export const accountTagsQuery = gql`
  query AccountTags {
    accountTags
  }
`;

export const chargeSourcesQuery = gql`
  query ChargeSources {
    chargeSources
  }
`;

export const masterAccountQuery = gql`
  query MasterAccount($id: ID!) {
    masterAccount(id: $id) {
      ...MasterAccountFields
    }
  }
  ${masterAccountFields}
`;

export const userMasterAccountQuery = gql`
  query UserMasterAccount {
    userMasterAccount {
      ...UserMasterAccountFields
    }
  }
  ${userMasterAccountFields}
`;

export const notificationsPortalUrlQuery = gql`
  query NotificationsPortalUrl($accountId: ID!) {
    notificationsPortalUrl(accountId: $accountId) {
      redirectUrl
    }
  }
`;

export const webhooksQuery = gql`
  query Webhooks($token: String, $pageSize: Int, $accountId: ID!) {
    webhooks(token: $token, pageSize: $pageSize, accountId: $accountId) {
      items {
        id
        accountId
        createdAt
        url
        eventTypes
        enabled
        updatedAt
      }
      nextToken
    }
  }
`;

export const restrictedKeywordsQuery = gql`
  query RestrictedKeywords {
    restrictedKeywords
  }
`;

export const websiteReportQuery = gql`
  query WebReport($baseUrl: String!) {
    webReport(baseUrl: $baseUrl) {
      title
      description
      summary
      pageCount
      failedPageCount
      uncheckedPageCount
      legalDetailsUrl
      status
      statusCode
      statusMessage
      riskCategory
      platform
    }
  }
`;

export const websiteKeywordsQuery = gql`
  query WebKeywords($baseUrl: String!, $pageSize: Int, $token: String) {
    webKeywords(baseUrl: $baseUrl, pageSize: $pageSize, token: $token) {
      items {
        accountId
        url
        createdAt
        expiresAt
        urlKeyword
        baseUrl
        keyword
        description
        updatedAt
        title
        context
        confirmed
        explanation
        incorrect
      }
      nextToken
    }
  }
`;

export const accountBalancesQuery = gql`
  query AccountBalances($accountId: ID!) {
    accountBalances(accountId: $accountId) {
      items {
        ...AccountBalancesFields
      }
    }
  }
`;

export const affiliatesQuery = gql`
  query Affiliates($input: AffiliateInput) {
    affiliates(input: $input) {
      items {
        ...AffiliateFields
      }
      links
    }
  }
  ${affiliateFields}
`;

export const affiliateQuery = gql`
  query Affiliate($affiliateId: ID!) {
    affiliate(affiliateId: $affiliateId) {
      ...AffiliateFields
    }
  }
  ${affiliateFields}
`;

export const affiliatePayoutMethodsQuery = gql`
  query AffiliatePayoutMethods($affiliateId: ID!) {
    affiliatePayoutMethods(affiliateId: $affiliateId) {
      ...PayoutMethodsFields
    }
  }
  ${payoutMethodsFields}
`;

export const affiliateMerchantsQuery = gql`
  query AffiliateMerchants($input: AffiliateMerchantInput) {
    affiliateMerchants(input: $input) {
      items {
        ...AffiliateMerchantFields
      }
      links
    }
  }
  ${affiliateMerchantFields}
`;

export const affiliateInvoicesQuery = gql`
  query AffiliateInvoices($taxId: String!) {
    affiliateInvoices(taxId: $taxId) {
      items {
        ...AffiliateInvoiceFields
      }
      total
    }
  }
  ${affiliateInvoiceFields}
`;

export const masterAccountEventsQuery = gql`
  query MasterAccountEvents($masterAccountId: ID!, $size: Int, $from: Int) {
    masterAccountEvents(masterAccountId: $masterAccountId, size: $size, from: $from) {
      items {
        id
        type
        accountId
        livemode
        objectId
        objectType
        object {
          ...MasterAccountEventObjectFields
        }
        createdAt
      }
      total
    }
  }
  ${masterAccountEventObjectFields}
`;

export const pointsOfSaleQuery = gql`
  query PointsOfSale($accountId: ID!, $storeId: ID, $pageSize: Int, $token: String) {
    pointsOfSale(accountId: $accountId, storeId: $storeId, pageSize: $pageSize, token: $token) {
      items {
        ...PointOfSaleFields
      }
      nextToken
    }
  }
  ${pointOfSaleFields}
`;

export const pointOfSaleQuery = gql`
  query PointOfSale($id: ID!, $accountId: ID!) {
    pointOfSale(id: $id, accountId: $accountId) {
      ...PointOfSaleFields
    }
  }
  ${pointOfSaleFields}
`;

export const blacklistItemsQuery = gql`
  query BlacklistItems($accountId: ID!, $pageSize: Int, $token: String, $type: BlacklistItemType) {
    blacklistItems(accountId: $accountId, pageSize: $pageSize, token: $token, type: $type) {
      items {
        ...BlacklistItemFields
      }
      nextToken
    }
  }
  ${blacklistItemFields}
`;

export const blacklistItemQuery = gql`
  query BlacklistItem($value: String!, $accountId: ID) {
    blacklistItem(value: $value, accountId: $accountId) {
      ...BlacklistItemFields
    }
  }
  ${blacklistItemFields}
`;

export const adminUserQuery = gql`
  query AdminUser($id: ID!) {
    adminUser(id: $id) {
      ...AdminUserFields
    }
  }
  ${adminUserFields}
`;

export const adminUsersQuery = gql`
  query AdminUsers($token: String, $limit: Int) {
    adminUsers(token: $token, limit: $limit) {
      items {
        ...AdminUserFields
      }
      nextToken
    }
  }
  ${adminUserFields}
`;

export const adminUserEventsQuery = gql`
  query AdminUserEvents($input: AdminUserEventsInput!) {
    adminUserEvents(input: $input) {
      items {
        ...AdminUserEventFields
      }
      nextToken
    }
  }
  ${adminUserEventFields}
`;

export const chargesExportsQuery = gql`
  query ChargesExports($token: String, $pageSize: Int) {
    chargesExports(token: $token, pageSize: $pageSize) {
      items {
        ...ChargesExportFields
      }
      nextToken
    }
  }
  ${chargesExportFields}
`;

export const exportSftpConfigQuery = gql`
  query ExportSftpConfig($id: ID!, $accountId: ID!) {
    exportSftpConfig(id: $id, accountId: $accountId) {
      ...SftpConfigExportFields
    }
  }
  ${sftpConfigExportFields}
`;

export const exportSftpConfigsQuery = gql`
  query ExportSftpConfigs($accountId: ID!) {
    exportSftpConfigs(accountId: $accountId) {
      items {
        ...SftpConfigExportFields
      }
    }
  }
  ${sftpConfigExportFields}
`;

export const codesQuery = gql`
  query Codes($accountId: ID!, $pointOfSaleId: String) {
    codes(accountId: $accountId, pointOfSaleId: $pointOfSaleId) {
      ...CodeFields
    }
  }
  ${codeFields}
`;

export const pibisiUsersQuery = gql`
  query PibisiUsers($accountId: ID!) {
    pibisiUsers(accountId: $accountId) {
      ...PibisiAccountFields
    }
  }
  ${pibisiAccountFields}
`;

export const pibisiEventsQuery = gql`
  query PibisiEvents($pibisiId: ID!) {
    pibisiEvents(pibisiId: $pibisiId) {
      items {
        ...PibisiEventFields
      }
      total
    }
  }
  ${pibisiEventFields}
`;

export const shortLinkQuery = gql`
  query ShortLink($url: String!, $accountId: ID!) {
    shortLink(url: $url, accountId: $accountId) {
      id
      url
      openCount
      createdAt
    }
  }
`;

export const getCapitalDataByAccountQuery = gql`
  query CapitalDataByAccount($accountId: ID!) {
    capitalDataByAccount(accountId: $accountId) {
      offer {
        loanAmount
        fundedAmount
        currencyISOCode
        fee
      }
      lead {
        onboardingStatus
        signUpUrl
      }
    }
  }
`;

export const getCapitalOnboardingDetailsQuery = gql`
  query GetCapitalOnboardingDetails($accountId: ID!) {
    capitalOnboardingDetails(accountId: $accountId) {
      leadId
      status
      onboardingStateReason
      organisationDetailsStepComplete
      paymentDataStepComplete
      offerStepComplete
      kycDocumentsStepComplete
      companyName
      stateUpdated
    }
  }
`;

export const capitalLoansQuery = gql`
  query CapitalLoans($accountId: ID!) {
    capitalLoans(accountId: $accountId) {
      items {
        id
        loanNumber
        status
        fundedAmount
        currentAmount
        originalAmount
        currencyISOCode
        sweep
        fundingDate
        maturityDate
        loanType
        aheadBehindValue
        aheadBehindPercentage
      }
    }
  }
`;

export const capitalLoanQuery = gql`
  query CapitalLoan($accountId: ID!, $loanId: ID!) {
    capitalLoan(accountId: $accountId, loanId: $loanId) {
      id
      loanNumber
      borrowerName
      status
      loanType
      originalAmount
      currentAmount
      currencyISOCode
      sweep
      discount
      expectedRepayment
      approvalDate
      maturityDate
      projectedRepaymentDate
      repaymentMethod
      repaymentFrequency
      fundingDetails {
        fundingAmount
        fundingDate
        amountFunded
      }
      documents {
        document {
          documentId
          documentName
          documentType
          dateUploaded
        }
        documentType
      }
    }
  }
`;

export const capitalLoanRepaymentsQuery = gql`
  query CapitalLoanRepayments($accountId: ID!, $loanId: ID!) {
    capitalLoanRepayments(accountId: $accountId, loanId: $loanId) {
      items {
        paymentDate
        repaymentAmount
        loanBalance
        totalCardTransactions
        sweep
      }
    }
  }
`;

export const downloadCapitalLoanDocumentMutation = gql`
  mutation DownloadCapitalLoanDocument($accountId: ID!, $loanId: ID!, $documentId: ID!) {
    downloadCapitalLoanDocument(accountId: $accountId, loanId: $loanId, documentId: $documentId) {
      downloadUrl
      expiresAt
    }
  }
`;
